import type { DayOffset, PlannerSport, SessionHour, TideState } from "./types";
import type { ConditionSafetyState } from "../conditions/safety-notes";
import type { FactorConfidence } from "../conditions/factor-explanations";
import {
  isSupProfileKey,
  type SupProfileConfidence,
  type SupProfileKey,
} from "../conditions/sport-profiles";

/** A sport-independent hour of raw conditions (shared by every sport at a spot). */
export type SessionDataHour = {
  hour: number;
  dayOffset: DayOffset;
  isDay: boolean;
  waveHeightM: number | null;
  windWaveHeightM?: number | null;
  swellPeriodS: number | null;
  swellDirDeg: number | null;
  windSpeedKmh: number;
  windDirDeg: number | null;
  windGustKmh: number | null;
  airTempC: number | null;
  waterTempC: number | null;
  tidePosition: number | null;
  tideState: TideState | null;
  weatherCode: number | null;
  /** Atmospheric visibility above the surface. */
  visibilityM: number | null;
  underwaterVisibilityM?: number | null;
  underwaterVisibilitySource?: "secchi" | "chlorophyll-inferred" | "unavailable";
  underwaterVisibilityProvider?: "copernicus" | "water-quality" | null;
  currentVelocityMs?: number | null;
  currentDirectionDeg?: null;
};

export type ScoreHour = {
  hour: number;
  dayOffset: DayOffset;
  score: number;
  safetyState?: "clear" | "flagged-red" | "flagged-amber" | "no-data";
  safetyNotes?: string[];
  confidence?: FactorConfidence;
  confidenceReasons?: string[];
};

export type SupProfileScoreHour = Omit<ScoreHour, "score"> & {
  score: number | null;
  confidence: SupProfileConfidence;
};

export type SupProfileSessionHour = Omit<SessionHour, "score"> & {
  score: number | null;
  confidence: SupProfileConfidence;
};

export type AvailableSupProfileSessionHour = SupProfileSessionHour & {
  score: number;
};

export function availableSupProfileHours(
  hours: readonly SupProfileSessionHour[],
): AvailableSupProfileSessionHour[] {
  return hours.filter(
    (hour): hour is AvailableSupProfileSessionHour => hour.score !== null,
  );
}

export type PlannerScoreHour = ScoreHour | SupProfileScoreHour;

export type NativeWindowScore = {
  dayOffset: DayOffset;
  startHour: number;
  endHour: number;
  score: number;
  /**
   * "clear" only from the engine's hourly v2 levels (SAIL-5): the revision 1
   * window scorer has no clear state, so its clean windows read "no-data".
   */
  safetyState?: "clear" | "flagged-red" | "flagged-amber" | "no-data";
  safetyNotes?: string[];
  /**
   * Present when the state was taken from the engine's stored hourly v2 levels
   * (sailing-safety.ts). Every hour of such a window was assessed, so "no-data"
   * there means a missing input, never an unknown red, and the day-level red
   * fallback does not apply.
   */
  safetyPolicy?: "v2";
};

export type NearbySpotData = {
  slug: string;
  name: string;
  distanceKm: number;
  lat: number;
  lng: number;
  /** Best-of-today/tomorrow 0-10 score keyed by scoring key. */
  bestScoreBySport: Record<string, number>;
};

export type WaterStatus = "clear" | "caution" | "poor" | "unknown";

/** One candidate spot in the cross-spot finder: the same raw bundle a spot's
 * own card uses, minus page-only extras, so the client can build windows with
 * the user's preferences and rank spots from one cacheable fetch. */
export type FinderSpotData = {
  slug: string;
  name: string;
  distanceKm: number;
  lat: number;
  lng: number;
  availableSports: PlannerSport[];
  beachFacingDeg: number | null;
  waterTempC: number | null;
  tideApproximate: boolean;
  hours: SessionDataHour[];
  scoresBySport: Record<string, PlannerScoreHour[]>;
  safetyNotesBySport?: Record<string, string[]>;
  safetyStateBySport?: Record<string, ConditionSafetyState>;
};

/** The bundle returned by GET /api/conditions/[slug]/session/finder. */
export type FinderData = {
  origin: { slug: string; name: string; lat: number; lng: number };
  radiusKm: number;
  generatedDate: string; // YYYY-MM-DD
  /** Present only after the server verifies named-public request authority. */
  availableSupProfiles?: SupProfileKey[];
  /** Nearest first; includes the origin itself at distance 0. */
  spots: FinderSpotData[];
};

/** The bundle returned by GET /api/conditions/[slug]/session and consumed client-side. */
export type SessionData = {
  slug: string;
  name: string;
  lat: number;
  lng: number;
  locationType: "surf" | "sailing";
  availableSports: PlannerSport[];
  beachFacingDeg: number | null;
  waterStatus: WaterStatus;
  waterTempC: number | null;
  generatedDate: string; // YYYY-MM-DD (today, local to the spot)
  tideApproximate: boolean;
  hours: SessionDataHour[];
  scoresBySport: Record<string, PlannerScoreHour[]>; // keyed by scoring key
  /** Present only after the server verifies named-public request authority. */
  availableSupProfiles?: SupProfileKey[];
  nativeWindowsBySport?: Record<string, NativeWindowScore[]>; // keyed by scoring key
  safetyNotesBySport: Record<string, string[]>;
  /** Optional for backwards compatibility with older cached payloads. */
  safetyStateBySport?: Record<string, ConditionSafetyState>;
  nearby: NearbySpotData[];
};

/** Resolve the exact scoring alias first, then the planner-sport fallback. */
export function safetyContextForSport(
  data: {
    safetyNotesBySport?: Record<string, string[]>;
    safetyStateBySport?: Record<string, ConditionSafetyState>;
  },
  scoringKey: string,
  plannerSport: PlannerSport,
): { safetyNotes: string[]; safetyState: ConditionSafetyState } {
  if (
    scoringKey === "sup-touring" ||
    scoringKey === "sup-surf" ||
    scoringKey === "sup-downwind"
  ) {
    return {
      safetyNotes: data.safetyNotesBySport?.[scoringKey] ?? [],
      safetyState: data.safetyStateBySport?.[scoringKey] ?? "no-data",
    };
  }
  return {
    safetyNotes:
      data.safetyNotesBySport?.[scoringKey] ??
      data.safetyNotesBySport?.[plannerSport] ??
      [],
    safetyState:
      data.safetyStateBySport?.[scoringKey] ??
      data.safetyStateBySport?.[plannerSport] ??
      "no-data",
  };
}

/**
 * Merge the sport-independent raw hours with a sport's score series into the
 * SessionHour[] the engine consumes. Legacy missing scores default to zero;
 * named SUP gaps remain explicitly unavailable. Accepts a spot page's
 * SessionData or a finder candidate (same raw shape).
 */
export function buildHoursForSport(
  data: Pick<SessionData, "hours" | "scoresBySport">,
  scoringKey: SupProfileKey,
): SupProfileSessionHour[];
export function buildHoursForSport<TKey extends string>(
  data: Pick<SessionData, "hours" | "scoresBySport">,
  scoringKey: TKey,
): TKey extends SupProfileKey ? SupProfileSessionHour[] : SessionHour[];
export function buildHoursForSport(
  data: Pick<SessionData, "hours" | "scoresBySport">,
  scoringKey: string,
): SessionHour[] | SupProfileSessionHour[] {
  const scoreMap = new Map<string, PlannerScoreHour>();
  for (const s of data.scoresBySport[scoringKey] ?? []) {
    scoreMap.set(`${s.dayOffset}:${s.hour}`, s);
  }
  if (isSupProfileKey(scoringKey)) {
    return data.hours.map((h): SupProfileSessionHour => {
      const scored = scoreMap.get(`${h.dayOffset}:${h.hour}`);
      const confidence = scored && "confidence" in scored &&
        (scored.confidence === "full" || scored.confidence === "partial")
        ? scored.confidence
        : "unavailable";
      const score = confidence !== "unavailable" &&
        typeof scored?.score === "number" && Number.isFinite(scored.score)
        ? scored.score
        : null;
      return {
        ...rawSessionHour(h),
        score,
        confidence,
        confidenceReasons: scored?.confidenceReasons ? [...scored.confidenceReasons] : [],
        ...(scored?.safetyState ? { safetyState: scored.safetyState } : {}),
        ...(scored?.safetyNotes ? { safetyNotes: [...scored.safetyNotes] } : {}),
      };
    });
  }
  return data.hours.map((h) => {
    const scored = scoreMap.get(`${h.dayOffset}:${h.hour}`);
    return {
      ...rawSessionHour(h),
      score: scored?.score ?? 0,
      confidence: scored?.confidence ?? "unavailable",
      confidenceReasons: scored?.confidenceReasons ? [...scored.confidenceReasons] : [],
      ...(scored?.safetyState ? { safetyState: scored.safetyState } : {}),
      ...(scored?.safetyNotes ? { safetyNotes: [...scored.safetyNotes] } : {}),
    };
  });
}

function rawSessionHour(h: SessionDataHour): Omit<SessionHour, "score"> {
  return {
    hour: h.hour,
    dayOffset: h.dayOffset,
    isDay: h.isDay,
    waveHeightM: h.waveHeightM,
    windWaveHeightM: h.windWaveHeightM ?? null,
    swellPeriodS: h.swellPeriodS,
    swellDirDeg: h.swellDirDeg,
    windSpeedKmh: h.windSpeedKmh,
    windDirDeg: h.windDirDeg,
    windGustKmh: h.windGustKmh,
    airTempC: h.airTempC,
    waterTempC: h.waterTempC,
    tidePosition: h.tidePosition,
    tideState: h.tideState,
    weatherCode: h.weatherCode,
    visibilityM: h.visibilityM,
    underwaterVisibilityM: h.underwaterVisibilityM ?? null,
    underwaterVisibilitySource: h.underwaterVisibilitySource ?? "unavailable",
    underwaterVisibilityProvider: h.underwaterVisibilityProvider ?? null,
    currentVelocityMs: h.currentVelocityMs ?? null,
    currentDirectionDeg: null,
  };
}
